/**
 * AuditLog — admin-only timeline of every important platform action.
 *
 * Pulls from /api/audit and /api/audit/summary. Filterable by action, severity,
 * resource type, actor. Each entry renders as a single timeline row.
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  ShieldCheck, ScrollText, AlertTriangle, AlertOctagon, Activity,
  UserCog, Send, FileSignature, Building2, RefreshCw, Filter,
  LogIn, FileText, Brain, Download, ChevronLeft, ChevronRight,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const ICONS = {
  workflow_approved: ShieldCheck,
  workflow_rejected: AlertOctagon,
  workflow_executed: ShieldCheck,
  tenant_invite_sent: Send,
  tenant_invite_accepted: ShieldCheck,
  tenant_invite_resent: Send,
  tenant_onboarded: Building2,
  user_role_changed: UserCog,
  user_active_changed: UserCog,
  agreement_created: FileSignature,
  auth_login_success: LogIn,
  auth_login_failed: AlertOctagon,
  pitch_submitted: FileText,
  qa_completed: Brain,
  psychometric_submitted: Brain,
};

const ACTIONS = [
  '', 'workflow_approved', 'workflow_rejected', 'workflow_executed',
  'tenant_invite_sent', 'tenant_invite_accepted', 'tenant_invite_resent', 'tenant_onboarded',
  'user_role_changed', 'user_active_changed', 'agreement_created',
  'auth_login_success', 'auth_login_failed',
  'pitch_submitted', 'qa_completed', 'psychometric_submitted',
];

const PAGE_SIZE = 25;

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function StatCard({ label, value, sub, icon: Icon, tone = 'violet' }) {
  // Bloomberg-style data readout for the 4 top stat cards.
  const accents = {
    violet: 'text-[var(--ve-violet-bright)]',
    rose:   'text-rose-300 dark:text-rose-300',
    amber:  'text-amber-300 dark:text-amber-300',
    slate:  'text-slate-200 dark:text-slate-200',
  };
  return (
    <div className="pal-card pal-grid-bg p-4">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="pal-bracket text-[9px]">[{label.toUpperCase()}]</div>
          <div className={`pal-mono text-3xl font-bold mt-2 tabular-nums truncate ${accents[tone] || accents.violet}`}>
            {value}
          </div>
          {sub && <div className="pal-mono text-[10px] mt-1 opacity-55 truncate">{sub}</div>}
        </div>
        <Icon size={18} className="opacity-50 shrink-0" />
      </div>
    </div>
  );
}

export default function AuditLog() {
  const { user } = useAuth();
  const isSystemAdmin = user?.role === 'system_admin';
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tenantOptions, setTenantOptions] = useState([]);
  const [filter, setFilter] = useState({
    action: '', severity: '', resource_type: '', tenant_id: '', from: '', to: '',
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => !!v));
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get('/audit/', { params }),
        api.get('/audit/summary'),
      ]);
      setItems(list);
      setSummary(sum);
      setPage(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load audit log.');
    } finally { setLoading(false); }
  };
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on plain async fetches */
  useEffect(() => { load(); }, [filter.action, filter.severity, filter.resource_type, filter.tenant_id, filter.from, filter.to]);

  // For system_admin only — fetch the distinct tenants once.
  useEffect(() => {
    if (!isSystemAdmin) return;
    api.get('/audit/tenants').then(({ data }) => setTenantOptions(data)).catch(() => {});
  }, [isSystemAdmin]);
  /* eslint-enable */

  const filtered = useMemo(() => {
    // Client-side date filtering (server doesn't yet support it).
    return items.filter((e) => {
      if (filter.from && (e.created_at || '') < filter.from) return false;
      if (filter.to && (e.created_at || '').slice(0, 10) > filter.to) return false;
      return true;
    });
  }, [items, filter.from, filter.to]);

  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const grouped = useMemo(() => {
    const map = new Map();
    for (const i of paged) {
      const k = (i.created_at || '').slice(0, 10);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(i);
    }
    return Array.from(map.entries());
  }, [paged]);

  const exportCsv = () => {
    if (!filtered.length) { toast.error('Nothing to export.'); return; }
    const header = ['created_at', 'action', 'severity', 'resource_type', 'resource_id',
                    'resource_label', 'actor_email', 'actor_role', 'metadata'];
    const escape = (s) => {
      if (s == null) return '';
      const str = typeof s === 'string' ? s : JSON.stringify(s);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const rows = filtered.map((e) => header.map((h) => escape(e[h])).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `vigilant-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filtered.length} events`);
  };

  return (
    <div className="space-y-5" data-testid="audit-log-page">
      {/* ── Header ── */}
      <div className="pal-card pal-grid-bg p-5 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="pal-bracket flex items-center gap-2 flex-wrap">
            [AUDIT LOG · IMMUTABLE]
            {isSystemAdmin && (
              <span className="pal-status pal-status-violet" data-testid="audit-super-view-badge">
                CROSS-TENANT · SUPER-VIEW
              </span>
            )}
          </div>
          <h1 className="display text-2xl md:text-3xl mt-1.5">
            Every action. Every actor. Forever auditable.
          </h1>
          <p className="text-sm mt-1.5 max-w-2xl opacity-70 pal-mono text-[11px] leading-relaxed">
            {isSystemAdmin
              ? 'Platform-wide chronological record across every tenant. Filter by tenant to narrow down. Required for QFC / family-office governance.'
              : 'A tamper-evident chronological record of approvals, role changes, invitations, and agreements. Required for QFC / family-office governance.'}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start shrink-0">
          <button onClick={load} data-testid="audit-refresh"
                  className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold border-white/10 text-slate-300 hover:bg-white/5 flex items-center gap-1.5 dark:text-slate-300">
            <RefreshCw size={11} /> REFRESH
          </button>
          <button onClick={exportCsv} data-testid="audit-export-csv"
                  className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] rounded border pal-mono font-bold border-[rgba(174,82,234,0.40)] text-[var(--ve-violet-bright)] hover:bg-[rgba(174,82,234,0.10)] flex items-center gap-1.5">
            <Download size={11} /> EXPORT · CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total events" value={summary.total.toLocaleString()} icon={Activity} tone="violet"/>
          <StatCard label="Critical" value={summary.critical} sub="security-relevant" icon={AlertOctagon} tone="rose"/>
          <StatCard label="Warnings" value={summary.warnings} sub="role changes · rejections" icon={AlertTriangle} tone="amber"/>
          <StatCard label="Top action"
                    value={summary.by_action[0]?.action.replace(/_/g, ' ') || '—'}
                    sub={`×${summary.by_action[0]?.count || 0}`}
                    icon={ScrollText} tone="slate"/>
        </div>
      )}

      {/* Cross-tenant breakdown (system_admin only) */}
      {isSystemAdmin && summary?.by_tenant?.length > 0 && (
        <div className="pal-card pal-grid-bg p-5" data-testid="audit-by-tenant-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="pal-bracket">[CROSS-TENANT · BREAKDOWN]</span>
              <div className="pal-mono text-[10px] opacity-55 mt-1">Click a row to filter the timeline.</div>
            </div>
            <Building2 size={14} className="text-[var(--ve-violet-bright)]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {summary.by_tenant.map((t) => {
              const isActive = filter.tenant_id === t.tenant_id;
              return (
                <button
                  key={t.tenant_id}
                  onClick={() => setFilter({ ...filter, tenant_id: isActive ? '' : t.tenant_id })}
                  data-testid={`audit-tenant-chip-${t.tenant_id}`}
                  className={`flex items-center justify-between gap-3 rounded border px-3 py-2 text-left transition ${
                    isActive
                      ? 'border-[var(--ve-violet)] bg-[rgba(174,82,234,0.10)]'
                      : 'border-[rgba(174,82,234,0.18)] hover:border-[var(--ve-violet)]/60 hover:bg-[rgba(174,82,234,0.05)]'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">{t.label}</div>
                    <div className="pal-mono text-[9px] opacity-50 truncate">{t.tenant_id}</div>
                  </div>
                  <div className="pal-mono text-lg font-bold tabular-nums text-[var(--ve-violet-bright)]">{t.count.toLocaleString()}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="pal-card pal-grid-bg p-4 flex flex-wrap items-center gap-2">
        <Filter size={13} className="text-[var(--ve-violet-bright)] mr-1" />
        <span className="pal-bracket text-[9px] mr-1">[FILTERS]</span>
        <FilterSelect
          value={filter.action}
          onChange={(e) => setFilter({ ...filter, action: e.target.value })}
          testId="audit-action-filter"
        >
          {ACTIONS.map((a) => <option key={a} value={a}>{a || 'All actions'}</option>)}
        </FilterSelect>
        <FilterSelect
          value={filter.severity}
          onChange={(e) => setFilter({ ...filter, severity: e.target.value })}
          testId="audit-severity-filter"
        >
          <option value="">All severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </FilterSelect>
        <FilterSelect
          value={filter.resource_type}
          onChange={(e) => setFilter({ ...filter, resource_type: e.target.value })}
          testId="audit-resource-filter"
        >
          <option value="">All resources</option>
          <option value="change_request">Workflows</option>
          <option value="tenant_invite">Invites</option>
          <option value="tenant">Tenants</option>
          <option value="user">Users</option>
          <option value="agreement">Agreements</option>
          <option value="startup">Startups</option>
        </FilterSelect>
        {isSystemAdmin && tenantOptions.length > 0 && (
          <FilterSelect
            value={filter.tenant_id}
            onChange={(e) => setFilter({ ...filter, tenant_id: e.target.value })}
            testId="audit-tenant-filter"
          >
            <option value="">All tenants</option>
            {tenantOptions.map((t) => (
              <option key={t.tenant_id} value={t.tenant_id}>{t.label}</option>
            ))}
          </FilterSelect>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 font-bold">FROM</label>
          <input
            type="date"
            value={filter.from ? filter.from.slice(0, 10) : ''}
            onChange={(e) => setFilter({ ...filter, from: e.target.value ? `${e.target.value}T00:00:00` : '' })}
            data-testid="audit-from-filter"
            className="rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-2 py-1 text-[11px] pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]"
          />
          <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 font-bold">TO</label>
          <input
            type="date"
            value={filter.to || ''}
            onChange={(e) => setFilter({ ...filter, to: e.target.value })}
            data-testid="audit-to-filter"
            className="rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-2 py-1 text-[11px] pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]"
          />
          {(filter.from || filter.to) && (
            <button
              onClick={() => setFilter({ ...filter, from: '', to: '' })}
              className="pal-mono text-[10px] uppercase tracking-[0.20em] text-[var(--ve-violet-bright)] font-bold hover:text-[var(--ve-violet-glow)]"
              data-testid="audit-clear-dates"
            >
              CLEAR
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div>
        {loading && <div className="pal-mono text-xs opacity-60">LOADING…</div>}
        {!loading && filtered.length === 0 && (
          <div className="pal-card pal-grid-bg p-8 text-center" data-testid="audit-empty">
            <ScrollText size={28} className="mx-auto opacity-30 mb-3"/>
            <div className="pal-mono text-sm opacity-60">NO AUDIT EVENTS MATCH THE CURRENT FILTERS</div>
          </div>
        )}
        {grouped.map(([day, evts]) => (
          <div key={day} className="mt-5 first:mt-0">
            <div className="pal-bracket text-[9px] mb-2">[{day}]</div>
            <div className="space-y-2">
              {evts.map((e) => {
                const Icon = ICONS[e.action] || Activity;
                const sevStatus = e.severity === 'critical' ? 'alert' : e.severity === 'warning' ? 'warn' : 'violet';
                return (
                  <div key={e.audit_id}
                       data-testid={`audit-row-${e.audit_id}`}
                       className="pal-card p-3 flex items-start gap-3">
                    <span className={`h-9 w-9 rounded flex items-center justify-center shrink-0 border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)]`}>
                      <Icon size={14} className="text-[var(--ve-violet-bright)]"/>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="pal-mono text-[12px] font-bold">{e.action.replace(/_/g, ' ').toUpperCase()}</span>
                        <span className={`pal-status pal-status-${sevStatus}`}>
                          {e.severity}
                        </span>
                        {isSystemAdmin && e.tenant_id && (
                          <span className="pal-status pal-status-violet"
                                title={`Tenant: ${e.tenant_id}`}
                                data-testid={`audit-row-tenant-${e.audit_id}`}>
                            {tenantOptions.find((t) => t.tenant_id === e.tenant_id)?.label || e.tenant_id}
                          </span>
                        )}
                        <span className="pal-mono text-[10px] opacity-55 ml-auto">{timeAgo(e.created_at)}</span>
                      </div>
                      {e.resource_label && (
                        <div className="pal-mono text-[11px] mt-1">
                          <span className="opacity-50 mr-1.5">{e.resource_type?.toUpperCase()}</span>
                          <span>{e.resource_label}</span>
                        </div>
                      )}
                      <div className="pal-mono text-[10px] opacity-65 mt-1">
                        ACTOR · {e.actor_email || e.actor_user_id || 'system'}
                        {e.actor_role && <span className="ml-2 text-[var(--ve-violet-bright)]">[{e.actor_role.toUpperCase()}]</span>}
                      </div>
                      {e.metadata && Object.keys(e.metadata).length > 0 && (
                        <details className="mt-2 pal-mono text-[10px] opacity-65">
                          <summary className="cursor-pointer hover:opacity-100">[METADATA]</summary>
                          <pre className="mt-1 p-2 rounded bg-[rgba(174,82,234,0.04)] border border-[rgba(174,82,234,0.15)] overflow-x-auto whitespace-pre-wrap text-[10px]">
                            {JSON.stringify(e.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="mt-5 pal-card p-3 flex items-center justify-between"
               data-testid="audit-pagination">
            <div className="pal-mono text-[11px] opacity-70">
              SHOWING <span className="text-[var(--ve-violet-bright)] font-bold">{(page - 1) * PAGE_SIZE + 1}</span>–
              <span className="text-[var(--ve-violet-bright)] font-bold">{Math.min(page * PAGE_SIZE, filtered.length)}</span> OF{' '}
              <span className="text-[var(--ve-violet-bright)] font-bold">{filtered.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="audit-page-prev"
                className="px-2.5 py-1 text-[10px] uppercase tracking-[0.20em] rounded border pal-mono font-bold border-[rgba(174,82,234,0.30)] hover:bg-[rgba(174,82,234,0.10)] disabled:opacity-40 flex items-center gap-1"
              >
                <ChevronLeft size={11} /> PREV
              </button>
              <span className="pal-mono px-2 text-[11px]">{page} / {totalPages}</span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="audit-page-next"
                className="px-2.5 py-1 text-[10px] uppercase tracking-[0.20em] rounded border pal-mono font-bold border-[rgba(174,82,234,0.30)] hover:bg-[rgba(174,82,234,0.10)] disabled:opacity-40 flex items-center gap-1"
              >
                NEXT <ChevronRight size={11} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Monospace filter <select> ──────────────────────────────────────── */
function FilterSelect({ value, onChange, testId, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-2.5 py-1.5 text-[10px] pal-mono font-bold uppercase tracking-[0.12em] focus:outline-none focus:border-[var(--ve-violet-bright)]"
    >
      {children}
    </select>
  );
}
